/**
 * useLinkVisualization — React hook managing the card link visualization
 * state machine with debounce, caching, and API integration.
 *
 * 5-phase state machine: idle → debouncing → loading → active → fading-out
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6,
 *              7.1, 7.2, 7.4, 7.5, 7.6
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import * as api from '../services/api-client';
import { LinkCache, VisibleLink } from '../services/link-cache';
import { filterVisibleLinks, ApiLinkItem, validateTimeout } from '../services/link-visual-attributes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OverlayPhase = 'idle' | 'debouncing' | 'loading' | 'active' | 'fading-out';

export interface UseLinkVisualizationOptions {
  readonly boardId: string;
  readonly inactivityTimeout?: number; // 1-10 seconds, default 3
}

export interface LinkVisualizationState {
  readonly activeCardId: string | null;
  readonly links: VisibleLink[];
  readonly isLoading: boolean;
  readonly isFadingOut: boolean;
  /** Whether second-hop chain expansion is active (shift held) */
  readonly isExpanded: boolean;
}

export interface UseLinkVisualizationReturn {
  readonly state: LinkVisualizationState;
  readonly handlers: {
    onCardMouseEnter: (cardId: string) => void;
    onCardMouseLeave: () => void;
    onBoardClick: (event: MouseEvent) => void;
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEBOUNCE_DELAY_MS = 150;
const FADE_OUT_DURATION_MS = 300;
const API_TIMEOUT_MS = 5000;
const DEFAULT_INACTIVITY_TIMEOUT_S = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampTimeout(value: number): number {
  if (validateTimeout(value)) return value;
  if (value < 1) return 1;
  if (value > 10) return 10;
  return DEFAULT_INACTIVITY_TIMEOUT_S;
}

/**
 * Returns the set of card IDs currently rendered on the board by querying
 * DOM elements with `[data-card-id]` attributes.
 */
function getVisibleCardIds(): ReadonlySet<string> {
  const elements = document.querySelectorAll('[data-card-id]');
  const ids = new Set<string>();
  elements.forEach((el) => {
    const cardId = el.getAttribute('data-card-id');
    if (cardId) ids.add(cardId);
  });
  return ids;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLinkVisualization(
  options: UseLinkVisualizationOptions
): UseLinkVisualizationReturn {
  const { boardId } = options;
  const inactivityTimeoutS = clampTimeout(options.inactivityTimeout ?? DEFAULT_INACTIVITY_TIMEOUT_S);
  const inactivityTimeoutMs = inactivityTimeoutS * 1000;

  // --- State ---
  const [phase, setPhase] = useState<OverlayPhase>('idle');
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [links, setLinks] = useState<VisibleLink[]>([]);
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // --- Refs for timers and abort controllers ---
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingCardIdRef = useRef<string | null>(null);
  const phaseRef = useRef<OverlayPhase>('idle');
  const activeCardIdRef = useRef<string | null>(null);
  const linksRef = useRef<VisibleLink[]>([]);
  const cacheRef = useRef<LinkCache>(new LinkCache());
  const previousCardInvalidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expandedRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    activeCardIdRef.current = activeCardId;
  }, [activeCardId]);

  useEffect(() => {
    linksRef.current = links;
  }, [links]);

  useEffect(() => {
    expandedRef.current = isExpanded;
  }, [isExpanded]);

  // --- Shift key tracking ---
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Shift') {
        setIsShiftHeld(true);
      }
    }
    function handleKeyUp(e: KeyboardEvent): void {
      if (e.key === 'Shift') {
        setIsShiftHeld(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // --- Expand to second hop when shift is pressed while links are active ---
  useEffect(() => {
    if (!isShiftHeld) return;
    if (phase !== 'active') return;
    if (isExpanded) return;
    if (!activeCardId || links.length === 0) return;

    // Expand all link types for discoverability (cycle prevention handles loops)
    const expandableLinks = links.filter((l) => (l.depth ?? 0) === 0);
    if (expandableLinks.length === 0) return;

    setIsExpanded(true);

    // Fetch second-hop links for each target card
    const visitedIds = new Set<string>([activeCardId, ...links.map((l) => l.targetCardId)]);

    async function expandChain(): Promise<void> {
      const secondHopLinks: VisibleLink[] = [];
      const visibleCardIds = getVisibleCardIds();

      for (const link of expandableLinks) {
        try {
          const response = await api.get<{ data: ApiLinkItem[] }>(
            `/v1/boards/${boardId}/cards/${link.targetCardId}/links`,
          );

          const filtered = filterVisibleLinks(response.data, visibleCardIds);
          for (const hop2Link of filtered) {
            // Skip if already visited (cycle prevention)
            if (visitedIds.has(hop2Link.targetCardId)) continue;

            visitedIds.add(hop2Link.targetCardId);
            secondHopLinks.push({
              ...hop2Link,
              depth: 1,
              sourceCardId: link.targetCardId,
            });
          }
        } catch {
          // Silently skip failed fetches
        }
      }

      if (secondHopLinks.length > 0) {
        setLinks((prev) => [...prev, ...secondHopLinks]);
      }
    }

    expandChain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShiftHeld, phase, isExpanded, activeCardId, links.length, boardId]);

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      clearAllTimers();
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Timer utilities ---
  function clearAllTimers(): void {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }

  function clearDebounceTimer(): void {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }

  function clearInactivityTimer(): void {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }

  function clearFadeTimer(): void {
    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }

  // --- Transition to idle ---
  function transitionToIdle(): void {
    clearAllTimers();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    pendingCardIdRef.current = null;
    setPhase('idle');
    setActiveCardId(null);
    setLinks([]);
    setIsExpanded(false);
  }

  // --- Schedule inactivity timeout ---
  function scheduleInactivityTimeout(): void {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      // Transition active → fading-out
      setPhase('fading-out');
      fadeTimerRef.current = setTimeout(() => {
        // Schedule invalidation of the card that just timed out
        const cardToInvalidate = activeCardIdRef.current;
        if (cardToInvalidate) {
          scheduleCacheInvalidation(cardToInvalidate);
        }
        transitionToIdle();
      }, FADE_OUT_DURATION_MS);
    }, inactivityTimeoutMs);
  }

  // --- Schedule cache invalidation for previous card ---
  function scheduleCacheInvalidation(cardId: string): void {
    // Cancel any pending invalidation for a different card
    if (previousCardInvalidationTimerRef.current !== null) {
      clearTimeout(previousCardInvalidationTimerRef.current);
      previousCardInvalidationTimerRef.current = null;
    }
    // Invalidate after inactivity timeout elapses
    previousCardInvalidationTimerRef.current = setTimeout(() => {
      cacheRef.current.invalidate(cardId);
      previousCardInvalidationTimerRef.current = null;
    }, inactivityTimeoutMs);
  }

  // --- Fetch links for a card ---
  async function fetchLinks(cardId: string): Promise<void> {
    // Check cache first
    const cached = cacheRef.current.get(cardId, inactivityTimeoutMs);
    if (cached !== null) {
      setActiveCardId(cardId);
      setLinks(cached);
      setPhase('active');
      scheduleInactivityTimeout();
      return;
    }

    // Set up API call with abort controller
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Set up 5-second timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, API_TIMEOUT_MS);

    setPhase('loading');
    setActiveCardId(cardId);
    setLinks([]);
    setIsExpanded(false);

    try {
      const response = await api.get<{ data: ApiLinkItem[] }>(
        `/v1/boards/${boardId}/cards/${cardId}/links`
      );

      clearTimeout(timeoutId);

      // Check if this request was aborted or if a different card is now pending
      if (controller.signal.aborted) return;
      if (pendingCardIdRef.current !== null && pendingCardIdRef.current !== cardId) return;

      const visibleCardIds = getVisibleCardIds();
      const visibleLinks = filterVisibleLinks(response.data, visibleCardIds);

      // Cache the result
      cacheRef.current.set(cardId, visibleLinks);

      setActiveCardId(cardId);
      setLinks(visibleLinks);
      setPhase('active');
      scheduleInactivityTimeout();
    } catch {
      clearTimeout(timeoutId);
      // On error or timeout, return to idle silently (Req 7.5)
      if (!controller.signal.aborted) {
        transitionToIdle();
      }
    }
  }

  // --- Handlers ---
  const onCardMouseEnter = useCallback(
    (cardId: string) => {
      const currentPhase = phaseRef.current;
      const currentActiveCardId = activeCardIdRef.current;

      // active → active: mouseenter different card triggers immediate replace
      if (currentPhase === 'active' && currentActiveCardId !== cardId) {
        clearAllTimers();
        // Schedule invalidation of the previous card
        if (currentActiveCardId) {
          scheduleCacheInvalidation(currentActiveCardId);
        }
        pendingCardIdRef.current = cardId;
        fetchLinks(cardId);
        return;
      }

      // active + same card: restart inactivity timer
      if (currentPhase === 'active' && currentActiveCardId === cardId) {
        scheduleInactivityTimeout();
        return;
      }

      // fading-out → debouncing: mouseenter interrupts fade
      if (currentPhase === 'fading-out') {
        clearFadeTimer();
        // Schedule invalidation of the fading-out card
        if (currentActiveCardId && currentActiveCardId !== cardId) {
          scheduleCacheInvalidation(currentActiveCardId);
        }
      }

      // idle / debouncing / fading-out → debouncing: start debounce
      clearDebounceTimer();
      clearInactivityTimer();
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      pendingCardIdRef.current = cardId;
      setPhase('debouncing');
      setActiveCardId(null);
      setLinks([]);

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        fetchLinks(cardId);
      }, DEBOUNCE_DELAY_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardId, inactivityTimeoutMs]
  );

  const onCardMouseLeave = useCallback(() => {
    const currentPhase = phaseRef.current;

    if (currentPhase === 'debouncing') {
      // Cancel debounce — stay in debouncing until timer fires or new hover
      // The spec says hover-leave starts inactivity timer only when active.
      // For debouncing, the timer continues; if it fires, it fetches.
      return;
    }

    if (currentPhase === 'active') {
      // Start inactivity timeout (Req 2.1)
      scheduleInactivityTimeout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactivityTimeoutMs]);

  const onBoardClick = useCallback(
    (event: MouseEvent) => {
      const currentPhase = phaseRef.current;
      if (currentPhase === 'idle') return;

      const target = event.target as HTMLElement;
      const cardElement = target.closest('[data-card-id]');

      if (!cardElement) {
        // Click outside any card — immediate dismiss (Req 2.6)
        const cardToInvalidate = activeCardIdRef.current;
        transitionToIdle();
        if (cardToInvalidate) {
          scheduleCacheInvalidation(cardToInvalidate);
        }
        return;
      }

      const clickedCardId = cardElement.getAttribute('data-card-id');
      const currentActiveCardId = activeCardIdRef.current;

      if (clickedCardId && clickedCardId !== currentActiveCardId) {
        // Click on non-active card — immediate dismiss (Req 2.3)
        transitionToIdle();
        if (currentActiveCardId) {
          scheduleCacheInvalidation(currentActiveCardId);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // --- Derived state ---
  const state: LinkVisualizationState = {
    activeCardId,
    links,
    isLoading: phase === 'loading',
    isFadingOut: phase === 'fading-out',
    isExpanded,
  };

  return {
    state,
    handlers: {
      onCardMouseEnter,
      onCardMouseLeave,
      onBoardClick,
    },
  };
}
